import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useRef, useState } from 'react'
import { BackofficePermissionsEnum as Permissions } from '../../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../../common/hooks/usePermissions'
import { customSwal } from '../../../../../config/swal'
import {
  ConfigDocumentKey,
  ConfigurationService,
} from '../../../../../services/ConfigurationService'

// ─── Types ────────────────────────────────────────────────────────────────────

interface IDocSlot {
  label: string
  key: ConfigDocumentKey
  file: File | null
  draftUrl: string | null
  publishedUrl: string | null
}

const INITIAL_DOCS: IDocSlot[] = [
  {
    label: 'Contrato de administración',
    key: 'contrato_administracion',
    file: null,
    draftUrl: null,
    publishedUrl: null,
  },
  {
    label: 'Reglamento de participación',
    key: 'reglamento_fondos_mutuos',
    file: null,
    draftUrl: null,
    publishedUrl: null,
  },
]

// ─── DocChip ──────────────────────────────────────────────────────────────────

const DocChip: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: '#EFF6FF',
      border: '1px solid #BFDBFE',
      borderRadius: 8,
      padding: '4px 10px',
      fontSize: 13,
      color: '#1D4ED8',
      cursor: 'pointer',
    }}
  >
    <i className='bi bi-file-earmark-text' style={{ fontSize: 14 }} />
    <span>{label}</span>
  </button>
)

// ─── DocSidebarSlot ───────────────────────────────────────────────────────────

const DocSidebarSlot: React.FC<{
  index: number
  slot: IDocSlot
  isLast: boolean
  onChange: (file: File | null) => void
  onPreviewDraft: () => void
  onPreviewPublished: () => void
  onDownloadDraft: () => void
  onDownloadPublished: () => void
}> = ({ index, slot, isLast, onChange, onPreviewDraft, onPreviewPublished, onDownloadDraft, onDownloadPublished }) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const actionRows = [
    { label: 'Guardado', hasUrl: !!slot.draftUrl, onPreview: onPreviewDraft, onDownload: onDownloadDraft },
    { label: 'Publicado', hasUrl: !!slot.publishedUrl, onPreview: onPreviewPublished, onDownload: onDownloadPublished },
  ]

  return (
    <div style={{ marginBottom: 20 }}>
      <div className='fw-bold fs-6 text-gray-800 mb-1'>
        {index + 1}. {slot.label}
      </div>
      <div className='text-muted fs-7 mb-3'>Sube el documento correspondiente.</div>

      <div style={{ marginBottom: 12 }}>
        {slot.file ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 13,
              color: '#1D4ED8',
              minWidth: 0,
            }}
          >
            <span style={{ flexShrink: 0, background: '#1D4ED8', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 4px' }}>
              nuevo
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {slot.file.name}
            </span>
            <button
              type='button'
              onClick={() => onChange(null)}
              style={{ flexShrink: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1 }}
            >
              <i className='bi bi-x' style={{ fontSize: 14, color: '#6B7280' }} />
            </button>
          </div>
        ) : slot.draftUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 13,
                color: '#15803D',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <span style={{ flexShrink: 0, background: '#15803D', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 4px' }}>
                doc.
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {slot.draftUrl.split('/').pop()?.split('?')[0] ?? slot.label}
              </span>
            </div>
            <button
              type='button'
              onClick={() => inputRef.current?.click()}
              style={{
                flexShrink: 0,
                background: 'none',
                border: '1px dashed #D1D5DB',
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 12,
                color: '#6B7280',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <i className='bi bi-arrow-repeat' style={{ fontSize: 13 }} />
              Reemplazar
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            style={{
              background: 'none',
              border: '1px dashed #D1D5DB',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <i className='bi bi-upload' style={{ fontSize: 14 }} />
            Seleccionar archivo
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {actionRows.map(({ label, hasUrl, onPreview, onDownload }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#9CA3AF', width: 68, flexShrink: 0 }}>
              {label}
            </span>
            <button
              type='button'
              onClick={onPreview}
              disabled={!hasUrl}
              style={{ background: 'none', border: 'none', padding: 0, cursor: hasUrl ? 'pointer' : 'not-allowed', fontSize: 12, color: hasUrl ? '#6B7280' : '#D1D5DB', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <i className='bi bi-search' style={{ fontSize: 12 }} />
              Vista previa
            </button>
            <button
              type='button'
              onClick={onDownload}
              disabled={!hasUrl}
              style={{ background: 'none', border: 'none', padding: 0, cursor: hasUrl ? 'pointer' : 'not-allowed', fontSize: 12, color: hasUrl ? '#6B7280' : '#D1D5DB', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <i className='bi bi-download' style={{ fontSize: 12 }} />
              Descargar
            </button>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type='file'
        style={{ display: 'none' }}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {!isLast && <hr style={{ marginTop: 20, borderColor: '#E5E7EB' }} />}
    </div>
  )
}

// ─── DocumentsSidebar ─────────────────────────────────────────────────────────

export const DocumentsSidebar: React.FC = () => {
  const { hasPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [docs, setDocs] = useState<IDocSlot[]>(INITIAL_DOCS)
  const [action, setAction] = useState<'saving' | 'publishing' | null>(null)

  const { data: configDocs } = useQuery({
    queryKey: ['config-documents'],
    queryFn: () => ConfigurationService().listConfigDocuments(),
  })

  const openSidebar = () => {
    setDocs(
      INITIAL_DOCS.map((doc) => {
        const fetched = (configDocs ?? []).find((d) => d.key === doc.key)
        return { ...doc, draftUrl: fetched?.draft ?? null, publishedUrl: fetched?.published ?? null }
      })
    )
    setOpen(true)
  }

  const closeSidebar = () => {
    if (action !== null) return
    setOpen(false)
  }

  const handlePreviewDraft = (slot: IDocSlot) => {
    if (slot.draftUrl) window.open(slot.draftUrl, '_blank')
  }

  const handlePreviewPublished = (slot: IDocSlot) => {
    if (slot.publishedUrl) window.open(slot.publishedUrl, '_blank')
  }

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleDownloadDraft = async (key: ConfigDocumentKey, label: string) => {
    try {
      const blob = await ConfigurationService().downloadConfigDocumentDraft(key)
      triggerDownload(blob, `${label} (borrador).pdf`)
    } catch {
      customSwal.fire({ title: 'Error al descargar el borrador', icon: 'error' })
    }
  }

  const handleDownloadPublished = async (key: ConfigDocumentKey, label: string) => {
    try {
      const blob = await ConfigurationService().downloadConfigDocumentPublished(key)
      triggerDownload(blob, `${label}.pdf`)
    } catch {
      customSwal.fire({ title: 'Error al descargar el documento publicado', icon: 'error' })
    }
  }

  const hasDirtyFiles = docs.some((d) => d.file !== null)

  const saveSidebar = async () => {
    setAction('saving')
    try {
      await Promise.all(
        docs.filter((d) => d.file !== null).map((d) => ConfigurationService().uploadConfigDocument(d.key, d.file!))
      )
      queryClient.invalidateQueries(['config-documents'])
      setOpen(false)
      customSwal.fire({ title: 'Documentos guardados correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar los documentos', icon: 'error' })
    } finally {
      setAction(null)
    }
  }

  const publishSidebar = async () => {
    setAction('publishing')
    try {
      const filesToSave = docs.filter((d) => d.file !== null)
      if (filesToSave.length > 0) {
        await Promise.all(
          filesToSave.map((d) => ConfigurationService().uploadConfigDocument(d.key, d.file!))
        )
      }
      const results = await Promise.allSettled(
        INITIAL_DOCS.map((d) => ConfigurationService().publishConfigDocument(d.key))
      )
      const hardErrors = results.filter(
        (r) => r.status === 'rejected' && r.reason?.response?.status !== 400
      )
      if (hardErrors.length > 0) throw new Error('Publish failed')
      queryClient.invalidateQueries(['config-documents'])
      setOpen(false)
      customSwal.fire({ title: 'Documentos publicados correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar los documentos', icon: 'error' })
    } finally {
      setAction(null)
    }
  }

  return (
    <>
      {/* ── Toolbar row (chips + Cargar button) ── */}
      <div className='d-flex align-items-center gap-6 mb-8'>
        <div>
          <div className='fw-bold fs-6 text-gray-500 text-uppercase mb-1'>Documentos</div>
          <div className='text-muted fs-7'>Visualizado en todos los fondos</div>
        </div>

        {hasPermission(Permissions.RECOMMENDED_CONFIG.DOCUMENT_UPLOAD) && (
          <button
            type='button'
            className='btn btn-sm'
            onClick={openSidebar}
            style={{ background: 'var(--color-primary-blue)', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Cargar
            <i className='bi bi-upload' style={{ fontSize: 13, color: '#fff' }} />
          </button>
        )}

        {hasPermission(Permissions.RECOMMENDED_CONFIG.DOCUMENT_SHOW) &&
          INITIAL_DOCS.map((doc) => {
            const fetched = (configDocs ?? []).find((d) => d.key === doc.key)
            const previewUrl = fetched?.published ?? fetched?.draft ?? null
            if (!previewUrl) return null
            return (
              <DocChip
                key={doc.key}
                label={doc.label}
                onClick={() => window.open(previewUrl, '_blank')}
              />
            )
          })}
      </div>

      {/* ── Sidebar drawer ── */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050 }}>
          <div onClick={closeSidebar} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />

          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 360,
              background: '#fff',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #E5E7EB' }}>
              <span className='fw-bold fs-4 text-gray-800'>Documentos</span>
              <button
                type='button'
                onClick={closeSidebar}
                disabled={action !== null}
                style={{ background: 'none', border: 'none', cursor: action !== null ? 'not-allowed' : 'pointer', padding: 4, lineHeight: 1 }}
              >
                <i className='bi bi-x-lg' style={{ fontSize: 16, color: '#6B7280' }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div
                style={{
                  border: '1.5px dashed #93C5FD',
                  borderRadius: 8,
                  background: '#EFF6FF',
                  padding: '14px 16px',
                  color: '#1D4ED8',
                  fontSize: 13,
                  textAlign: 'center',
                  marginBottom: 24,
                }}
              >
                Carga los documentos del sistema.
              </div>

              {docs.map((slot, i) => (
                <DocSidebarSlot
                  key={slot.key}
                  index={i}
                  slot={slot}
                  isLast={i === docs.length - 1}
                  onChange={(file) => setDocs((prev) => prev.map((s, idx) => (idx === i ? { ...s, file } : s)))}
                  onPreviewDraft={() => handlePreviewDraft(slot)}
                  onPreviewPublished={() => handlePreviewPublished(slot)}
                  onDownloadDraft={() => handleDownloadDraft(slot.key, slot.label)}
                  onDownloadPublished={() => handleDownloadPublished(slot.key, slot.label)}
                />
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              {hasPermission(Permissions.RECOMMENDED_CONFIG.SAVE) && (
                <button
                  type='button'
                  className='btn'
                  onClick={saveSidebar}
                  disabled={action !== null || !hasDirtyFiles}
                  style={{ background: 'var(--color-primary-blue)', color: '#fff', fontWeight: 600 }}
                >
                  {action === 'saving' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Guardando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              )}
              {hasPermission(Permissions.RECOMMENDED_CONFIG.PUBLISH) && (
                <button
                  type='button'
                  className='btn'
                  onClick={publishSidebar}
                  disabled={action !== null}
                  style={{ background: 'var(--color-primary-blue)', color: '#fff', fontWeight: 600 }}
                >
                  {action === 'publishing' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Publicando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Publicar'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
